import { Dialog, DialogPanel } from '@headlessui/react'
import { IoMdClose } from 'react-icons/io'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useRef } from 'react'
import { ApiPut } from '../../helper/axiosFunctions'
import { commonToast } from '../../helper/Toast'
import Loading from '../Loading'
import Select from 'react-select'

const Input = ({ label, name, type = 'text', value, onChange, innerRef }) => (
    <div className="mb-3">
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
        </label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            ref={innerRef}
            className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none placeholder:text-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            autoComplete="off"
        />
    </div>
)

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '45px',
        borderRadius: '0.5rem',
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.2)' : 'inset 0 1px 2px rgba(0,0,0,0.1)'
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 })
}

const GenderSelect = ({ value, onChange, genderOptions }) => (
    <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
            Gender
        </label>
        <Select
            options={genderOptions}
            value={value}
            onChange={onChange}
            classNamePrefix="react-select"
            styles={customSelectStyles}
        />
    </div>
)

export default function EditStudentModel({ open, setOpen, student = {}, onUpdated }) {
    const { t } = useTranslation()
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        preferredLanguage: '',
        countryCode: '',
        nickname: '',
        gender: '',
        birthDate: '',
    })
    const [submitting, setSubmitting] = useState(false)
    const firstNameRef = useRef(null)

    useEffect(() => {
        if (open && student) {
            setFormData({
                firstName: student.firstName || '',
                lastName: student.lastName || '',
                email: student.email || '',
                preferredLanguage: student.preferredLanguage || '',
                countryCode: student.countryCode || '',
                nickname: student.nickname || '',
                gender: student.gender || '',
                birthDate: student.birthDate || '',
            })
        }
    }, [open, student])

    const handleClose = () => setOpen(false)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async () => {
        if (!student?.studentId) return
        setSubmitting(true)
        try {
            const payload = {
                studentId: student.studentId,
                schoolId: student.schoolId,
                ...formData,
            }
            await ApiPut('/admins/students', payload)
            commonToast(t('student_update_success') || 'Student updated successfully', 'success')
            onUpdated && onUpdated()
            setOpen(false)
        } catch (error) {
            commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const genderOptions = [
        { value: 'MALE', label: t('male') || 'MALE' },
        { value: 'FEMALE', label: t('female') || 'FEMALE' },
        { value: 'UNSPECIFIED', label: t('unspecified') || 'UNSPECIFIED' },
    ]

    return (
        <Dialog
            open={open}
            as="div"
            className="relative z-30 focus:outline-none"
            initialFocus={firstNameRef}
            onClose={handleClose}
        >
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 bg-black/35 backdrop-blur-sm">
                    <DialogPanel
                        transition
                        className="w-full relative max-w-md !rounded-3xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
                    >
                        <div className="p-6 relative z-10">
                            <h1 className="text-xl font-semibold mb-4">{t('edit_student') || 'Edit Student'}</h1>
                            <IoMdClose
                                className="absolute right-5 top-6 cursor-pointer"
                                fontSize={22}
                                onClick={handleClose}
                            />
                            <div className="mt-2">
                                <Input label={t('first_name') || 'First Name'} name="firstName" innerRef={firstNameRef} value={formData.firstName} onChange={handleChange} />
                                <Input label={t('last_name') || 'Last Name'} name="lastName" value={formData.lastName} onChange={handleChange} />
                                <Input label={t('email') || 'Email'} name="email" type="email" value={formData.email} onChange={handleChange} />
                                <Input label={t('preferred_language') || 'Preferred Language'} name="preferredLanguage" value={formData.preferredLanguage} onChange={handleChange} />
                                <Input label={t('country_code') || 'Country Code'} name="countryCode" value={formData.countryCode} onChange={handleChange} />
                                <GenderSelect
                                    genderOptions={genderOptions}
                                    value={genderOptions.find((o) => o.value === formData.gender) || null}
                                    onChange={(option) => setFormData((prev) => ({ ...prev, gender: option.value }))}
                                />
                                <Input label={t('birth_date') || 'Birth Date'} name="birthDate" type="date" value={formData.birthDate} onChange={handleChange} />
                                <Input label={t('nickname') || 'Nickname'} name="nickname" value={formData.nickname} onChange={handleChange} />
                            </div>
                            <button
                                onClick={handleSubmit}
                                className="primary-btn w-full mt-5"
                                disabled={submitting}
                            >
                                {submitting ? <Loading /> : t('save') || 'Save'}
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
} 